import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs/operators';
import {
  Event,
  initialize,
  Options,
  Result as HarnessFeatureFlagClient,
  Target as HarnessClientTarget,
  VariationValue,
} from '@harnessio/ff-javascript-client-sdk';
import { map as lodashMap } from 'lodash';

import { $user } from '../../../../shared/constants/user';
import { SharedCommonUtility } from '../../../../shared/utils/common.utility';
import { SharedTextUtility } from '../../../../shared/utils/text.utility';
import { IUserServerResponse } from '../../../../shared/interfaces/user.interface';
import { UserContextAttributes } from '../../../../shared/interfaces/feature-flag.interface';
import { AppConfigService } from '../app-config.service';
import { UserService } from '../user.service';
import { $securityGroup } from '../../../../shared/constants/security-group';
import { ITenantInfoWithPackageInfo } from '../../../../shared/interfaces/tenant.interface';
import { $tenant } from '../../../../shared/constants/tenant';
import { environment } from '../../../environments/environment';
import { EnvironmentType, GlobalFeatureFlagDefaults } from '../../shared/constants';
import { E2eFeatureFlagMock } from '../../../../shared/constants/e2e-feature-flag.mock';
import {
  E2E_FF_ANONYMOUS_OVERRIDES_STORAGE_KEY,
  E2eFeatureFlagOverrideMap,
} from '../../../../shared/interfaces/e2e-feature-flag-override.interface';
import { E2eFeatureFlagOverridesApi } from '../rest/e2e-feature-flag-overrides.api';
import { AngularUtility } from '../../utility/angular.utility';

type FeatureFlagClientTarget = HarnessClientTarget & UserContextAttributes;
const LEVEL_ACCESS_PLATFORM_CLIENT = 'level-access-platform-client';
const OPTION_CONFIG: Options = {
  // Setting to true to display information logs (ie event streams, flags) in the console
  debug: false,
  streamEnabled: true,
};
const MAX_RETRIES: number = 3;

interface IHarnessInitOptions {
  init: boolean;
  anonymous: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService implements OnDestroy {
  private subscriptions: Subscription = new Subscription();
  private _clientInitialized$: BehaviorSubject<IHarnessInitOptions> = new BehaviorSubject<IHarnessInitOptions>(void 0);
  private readonly authorizedClientInitialized$: Observable<boolean>;
  private readonly clientInitialized$: Observable<boolean>;
  private client: HarnessFeatureFlagClient;
  private _flagsChange$: BehaviorSubject<void> = new BehaviorSubject<void>(void 0);
  private _attempts: number;
  private _e2eOverrides: E2eFeatureFlagOverrideMap = {};

  constructor(
    private appConfigService: AppConfigService,
    private userService: UserService,
    private e2eFeatureFlagOverridesApi: E2eFeatureFlagOverridesApi,
  ) {
    const userIsEqual = (userA: IUserServerResponse, userB: IUserServerResponse): boolean =>
      userA?.[$user._id] === userB?.[$user._id];

    this.subscriptions.add(
      this.userService.userDataChanged$
        .pipe(
          distinctUntilChanged(userIsEqual),
          switchMap(
            (user: IUserServerResponse): Observable<(IUserServerResponse | ITenantInfoWithPackageInfo)[]> =>
              this.userService.currentTenant$.pipe(
                map((tenant: ITenantInfoWithPackageInfo | null) => [user, tenant]),
                catchError(() => of([user, null])),
              ),
          ),
        )
        .subscribe(this.identify.bind(this)),
    );

    this.authorizedClientInitialized$ = this._clientInitialized$.pipe(
      filter((initOptions: IHarnessInitOptions) => {
        return SharedCommonUtility.notNullish(initOptions?.anonymous);
      }),
      filter((initOptions: IHarnessInitOptions) => !initOptions.anonymous),
      map((initOptions: IHarnessInitOptions): boolean => initOptions.init),
      AngularUtility.shareRef(),
    );

    this.clientInitialized$ = this._clientInitialized$.pipe(
      map((initOptions: IHarnessInitOptions): boolean => initOptions?.init ?? false),
      AngularUtility.shareRef(),
    );
  }

  /**
   * This method is responsible for identifying and initializing the client target context information.
   * Once identified, the client can listen for the different events triggered by the SDK.
   * If client is null, the method will attempt to reinitialize it accordingly.
   *
   * @param array user {IUserServerResponse} data to be mapped as the target attributes during client initialization.
   *              tenant {ITenantInfoWithPackageInfo} tenant data to be mapped as the target attributes during client initialization.
   * Targets identifier must be unique and required.
   * @private
   */
  private identify([user, tenant]: [IUserServerResponse, ITenantInfoWithPackageInfo]): void {
    const target: FeatureFlagClientTarget = SharedCommonUtility.notNullish(user)
      ? {
          identifier: `userId-${user[$user._id]}`,
          name: SharedCommonUtility.notNullish(user[$user.displayName])
            ? SharedTextUtility.convertToHarnessAcceptableFormat(user[$user.displayName])
            : '',
          anonymous: false,
          attributes: {
            service: LEVEL_ACCESS_PLATFORM_CLIENT,
            userId: user[$user._id],
            email: user[$user.email],
            groups: lodashMap(user[$user.groups] ?? [], $securityGroup.name).join(','),
            tenantName: tenant?.[$tenant.name],
            tenantId: tenant?.[$tenant._id],
            packageId: tenant?.packageId,
            packageName: tenant?.packageName,
            packageAddonNames: lodashMap(tenant?.packageAddons ?? [], 'name'),
            packageAddonIds: lodashMap(tenant?.packageAddons ?? [], 'id'),
          },
        }
      : {
          identifier: 'anonymous',
          name: 'anonymous',
          anonymous: true,
          attributes: {
            service: LEVEL_ACCESS_PLATFORM_CLIENT,
          },
        };

    this._attempts = 0;
    this.initialize(target);
  }

  private initialize(target: FeatureFlagClientTarget): void {
    // for e2es, if you're logged in to the platform, you're also logged into to harness
    // otherwise, you're an anonymous user on harness
    if (environment.environmentType === EnvironmentType.QA) {
      this.client = {
        off: (): void => {},
        close: (): void => {},
      } as HarnessFeatureFlagClient;

      this.loadE2eOverrides(target);

      this._clientInitialized$.next({
        init: true,
        anonymous: target?.anonymous ?? true,
      });
      this._flagsChange$.next();
      this._attempts = 0;
      return;
    }
    this.client = initialize(this.appConfigService.getHarnessSDKClientKey(), target, OPTION_CONFIG);

    // Event happens when connection to server is established
    // flags contains all evaluations against SDK key
    this.client.on(Event.READY, () => {
      this._clientInitialized$.next({
        init: true,
        anonymous: target?.anonymous ?? true,
      });
      this._flagsChange$.next();
      this._attempts = 0;

      // Event happens when a changed event is pushed.
      // flag contains information about the updated feature flag
      // Listen to CHANGED events when READY happens since the event happens when initializing FF values as well.
      this.client.on(Event.CHANGED, () => {
        this._flagsChange$.next();
      });
    });

    this.client.on(Event.ERROR_AUTH, (err: unknown) => {
      if (this._attempts >= MAX_RETRIES) {
        this._clientInitialized$.next({
          init: false,
          anonymous: target?.anonymous ?? true,
        });
        console.warn(
          `[FeatureFlagService.initialize] Unable to initialize after ${this._attempts} attempts. Switching to default flag values.`,
        );
        return;
      }

      const errorMessage: string = (err as ErrorEvent)?.error?.message ?? (err as TypeError)?.message;
      setTimeout(() => {
        this._attempts++;
        console.warn(`[FeatureFlagService.initialize] Client initialize error '${errorMessage}' retry ${this._attempts}`, err);
        this.initialize(target);
      }, 1000);
    });
  }

  /**
   * An observable method that evaluates and returns the flag's variation value.
   *
   * @param flag - String representing the flag value
   * @param defaultValue - Boolean representing default value for the flag variation.
   * @param allowUnauthorized - Boolean allow unauthorized checks
   * Note: During a network connection error, the default value will be respected.
   * @returns an Observable variation value of boolean type
   */
  public variation$(flag: string, defaultValue?: boolean, allowUnauthorized: boolean = false): Observable<VariationValue> {
    // Mock the Feature Flag response when the client is running under the e2e environment
    if (environment.environmentType === EnvironmentType.QA) {
      return of(this.resolveE2eFlag(flag, defaultValue));
    }

    if (SharedCommonUtility.isNullish(this.client)) {
      console.warn(
        `[FeatureFlagService.variation$] Client is not initialized. Returning flag: ${flag} with a default value of ${defaultValue}`,
      );
    }

    // eslint-disable-next-line no-param-reassign
    defaultValue = GlobalFeatureFlagDefaults[flag] ?? defaultValue;

    return combineLatest([
      allowUnauthorized ? this.clientInitialized$ : this.authorizedClientInitialized$,
      this._flagsChange$,
    ]).pipe(
      shareReplay(),
      map(([clientInitialized, _]: [boolean, void]): boolean => {
        return clientInitialized;
      }),
      filter(SharedCommonUtility.notNullish),
      map((clientInitialized: boolean) => {
        if (!clientInitialized) {
          return defaultValue;
        }
        return this.client.variation(flag, defaultValue);
      }),
    );
  }

  /**
   * Evaluates and returns the flag's variation value.
   *
   * @param flag - String representing the flag value
   * @param defaultValue - Boolean representing default value for the flag variation.
   * @param allowUnauthorized - Boolean allow unauthorized checks
   * Note: During a network connection error, the default value will be respected.
   * @returns variation value of boolean type
   */
  public variation(flag: string, defaultValue?: boolean, allowUnauthorized: boolean = false): boolean {
    // Mock the Feature Flag response when the client is running under the e2e environment
    if (environment.environmentType === EnvironmentType.QA) {
      return this.resolveE2eFlag(flag, defaultValue);
    }

    // eslint-disable-next-line no-param-reassign
    defaultValue = GlobalFeatureFlagDefaults[flag] ?? defaultValue;

    const clientIsNotInitialized: boolean = !this._clientInitialized$.value?.init;
    const clientIsNotAuthorized: boolean = this._clientInitialized$.value?.anonymous;
    const authorizationRequired: boolean = !allowUnauthorized;

    if (clientIsNotInitialized || (authorizationRequired && clientIsNotAuthorized)) {
      return defaultValue;
    }

    return this.client.variation(flag, defaultValue) as boolean;
  }

  /**
   * This method is used to listen to any flag events in the stream.
   */
  public get flagsChange$(): Observable<void> {
    return this._flagsChange$.asObservable();
  }

  public get isAuthorizedClientInitialized$(): Observable<boolean> {
    return this.authorizedClientInitialized$;
  }

  public get isClientInitialized$(): Observable<boolean> {
    return this.clientInitialized$;
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.client?.off(); // remove event listeners
    this.client?.close(); // close application
  }

  private resolveE2eFlag(flag: string, defaultValue?: boolean): boolean {
    const returnDefault: boolean = defaultValue ?? false;
    if (SharedCommonUtility.notNullish(this._e2eOverrides[flag])) {
      return this._e2eOverrides[flag];
    }
    return SharedCommonUtility.notNullish(E2eFeatureFlagMock[flag]) ? E2eFeatureFlagMock[flag] : returnDefault;
  }

  /**
   * Reads anonymous-scope overrides written by e2e tests via
   * `E2eAnonymousFeatureFlagOverrides`. Anonymous can't hit the API endpoint
   * (it requires `userId`), so the contract between test helper and client is
   * `localStorage` under `E2E_FF_ANONYMOUS_OVERRIDES_STORAGE_KEY`.
   */
  private readAnonymousOverridesFromLocalStorage(): E2eFeatureFlagOverrideMap {
    try {
      const raw: string | null = window.localStorage.getItem(E2E_FF_ANONYMOUS_OVERRIDES_STORAGE_KEY);
      if (SharedCommonUtility.isNullish(raw) || raw.length === 0) {
        return {};
      }
      const parsed: unknown = JSON.parse(raw);
      if (SharedCommonUtility.isNullish(parsed) || typeof parsed !== 'object') {
        return {};
      }
      return parsed as E2eFeatureFlagOverrideMap;
    } catch (err: unknown) {
      console.warn('[FeatureFlagService.readAnonymousOverridesFromLocalStorage] failed to read overrides', err);
      return {};
    }
  }

  private loadE2eOverrides(target: FeatureFlagClientTarget): void {
    if (target?.anonymous) {
      this._e2eOverrides = this.readAnonymousOverridesFromLocalStorage();
      this._flagsChange$.next();
      return;
    }

    const tenantId: string | undefined = (target?.attributes as UserContextAttributes | undefined)?.tenantId;

    this.subscriptions.add(
      this.e2eFeatureFlagOverridesApi.getForCurrentUser(tenantId).subscribe({
        next: (response: { flags: E2eFeatureFlagOverrideMap }) => {
          this._e2eOverrides = response?.flags ?? {};
          this._flagsChange$.next();
        },
        error: (err: unknown) => {
          console.warn('[FeatureFlagService.loadE2eOverrides] could not fetch overrides', err);
          this._e2eOverrides = {};
        },
      }),
    );
  }
}
